import useSWR from "swr";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { fetcher } from "@/lib/api";

const marcar = (texto, tramos) => {
  if (!tramos?.length) return texto;
  const palabras = tramos.map((t) => t.split(" ").slice(0, 6).join(" "));
  let salida = texto;
  palabras.forEach((frase) => {
    const regex = new RegExp(frase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    salida = salida.replace(regex, (m) => `«®${m}®»`);
  });
  return salida;
};

const Resaltado = ({ texto, tramos }) => (
  <p className="text-sm leading-relaxed whitespace-pre-wrap">
    {marcar(texto, tramos).split(/«®|®»/).map((parte, i) =>
      i % 2 === 1
        ? <mark key={i} className="bg-destructive/20 text-foreground px-0.5">{parte}</mark>
        : <span key={i}>{parte}</span>)}
  </p>
);

export const CompararNota = ({ articleId }) => {
  const { data } = useSWR(`/articles/${articleId}/compare`, fetcher);
  if (!data) return <p className="text-sm text-muted-foreground">Comparando…</p>;

  const alto = data.similarity >= 60 || data.reused_count >= 8;
  return (
    <div data-testid="compare-view">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Badge variant={alto ? "destructive" : "default"} data-testid="compare-similarity">
          {data.similarity}% de coincidencia
        </Badge>
        <Badge variant="outline" data-testid="compare-reused">
          {data.reused_count} tramo(s) literal(es)
        </Badge>
        <span className="text-xs mono text-muted-foreground">
          fuente {data.source_words} palabras · redacción {data.body_words} palabras
        </span>
        {data.source_url && (
          <a href={data.source_url} target="_blank" rel="noreferrer"
             className="text-xs flex items-center gap-1 hover:text-primary"
             data-testid="compare-source-link">
            <ExternalLink className="h-3.5 w-3.5" />{data.source_name || "ver fuente"}
          </a>
        )}
      </div>

      {alto && (
        <div className="flex items-start gap-2 border border-destructive/40 bg-destructive/5 p-3 mb-4"
             data-testid="compare-warning">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-xs">
            La redacción reutiliza demasiado texto de la fuente. Reescriba los tramos marcados en rojo
            antes de publicar.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-border p-4 bg-muted/20">
          <div className="kicker mb-2">Nota de la fuente</div>
          {data.source_title && (
            <p className="serif text-lg font-bold leading-tight mb-2">{data.source_title}</p>
          )}
          <div className="max-h-[60vh] overflow-y-auto" data-testid="compare-source-text">
            {data.source_text
              ? <Resaltado texto={data.source_text} tramos={data.reused} />
              : <p className="text-sm text-muted-foreground">
                  No se guardó el texto original de esta nota (fue creada antes de esta función).
                </p>}
          </div>
        </div>
        <div className="border border-border p-4">
          <div className="kicker mb-2">Nota procesada</div>
          <p className="serif text-lg font-bold leading-tight mb-2">{data.title}</p>
          <div className="max-h-[60vh] overflow-y-auto" data-testid="compare-body-text">
            <Resaltado texto={data.body_text} tramos={data.reused} />
          </div>
        </div>
      </div>
    </div>
  );
};
